import React, { useState } from 'react';
import API_BASE from '../api';

function StatementAnalyser({ setError, setSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [statement, setStatement] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.pdf')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
      setFile(null);
    }
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Please select a PDF file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/api/analyze-statement`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze statement');
      }

      const data = await response.json();
      setAnalysis(data.analysis);
      setStatement(data.statement);
      setSuccess(`Successfully analyzed statement with ${data.analysis.totalTransactions} transactions`);
    } catch (err) {
      setError(err.message || 'Error analyzing statement');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setAnalysis(null);
    setStatement(null);
    setSelectedTransactions(new Set());
    setError(null);
  };

  const handleTransactionSelect = (idx) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(idx)) {
      newSelected.delete(idx);
    } else {
      newSelected.add(idx);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAll = () => {
    if (statement && statement.transactions) {
      if (selectedTransactions.size === statement.transactions.length) {
        // Deselect all
        setSelectedTransactions(new Set());
      } else {
        // Select all
        const allIndices = new Set(statement.transactions.map((_, idx) => idx));
        setSelectedTransactions(allIndices);
      }
    }
  };

  const getSelectionStats = () => {
    if (!statement || !statement.transactions || selectedTransactions.size === 0) {
      return null;
    }

    let totalDebit = 0;
    let totalCredit = 0;
    let debitCount = 0;
    let creditCount = 0;

    selectedTransactions.forEach(idx => {
      const txn = statement.transactions[idx];
      if (txn.type === 'credit') {
        totalCredit += txn.amount;
        creditCount++;
      } else {
        totalDebit += txn.amount;
        debitCount++;
      }
    });

    return {
      totalDebit,
      totalCredit,
      debitCount,
      creditCount,
      total: totalCredit - totalDebit
    };
  };

  return (
    <div className="statement-analyser">
      <div className="upload-section card">
        <h2>📄 Upload Bank Statement</h2>
        <p style={{ color: '#666', marginBottom: '15px' }}>
          Upload your bank or credit card statement (PDF format) to get a detailed expense analysis
        </p>
        
        <div className="file-input-wrapper" onClick={handleFileClick} style={{ cursor: 'pointer' }}>
          <input 
            ref={fileInputRef}
            type="file" 
            accept=".pdf" 
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <p style={{ margin: '10px 0', color: '#667eea', fontWeight: '500' }}>
            📁 Click to select PDF file or drag & drop
          </p>
          {file && (
            <p style={{ color: '#28a745', marginTop: '10px', fontWeight: '500' }}>
              ✓ Selected: {file.name}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <button 
            onClick={handleAnalyze} 
            disabled={!file || loading}
            style={{ flex: 1, opacity: !file || loading ? 0.6 : 1, cursor: !file || loading ? 'not-allowed' : 'pointer' }}
          >
            {loading ? '⏳ Analyzing...' : '🔍 Analyze Statement'}
          </button>
          <button 
            onClick={handleClear}
            style={{ background: '#dc3545', flex: 0.5 }}
          >
            Clear
          </button>
        </div>
      </div>

      {analysis && (
        <div className="analysis-results">
          <div className="card">
            <h2>📊 Statement Summary</h2>
            <div className="summary-grid">
              <div className="summary-item">
                <label>Total Transactions</label>
                <p className="big-number">{analysis.totalTransactions}</p>
              </div>
              <div className="summary-item">
                <label>Total Income</label>
                <p className="big-number positive">+₹ {analysis.totalIncome.toFixed(2)}</p>
              </div>
              <div className="summary-item">
                <label>Total Expenses</label>
                <p className="big-number negative">-₹ {analysis.totalExpenses.toFixed(2)}</p>
              </div>
              <div className="summary-item">
                <label>Net Balance</label>
                <p className={`big-number ${analysis.netBalance >= 0 ? 'positive' : 'negative'}`}>
                  {analysis.netBalance >= 0 ? '+' : '-'}₹ {Math.abs(analysis.netBalance).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>💰 Income Sources</h2>
            {analysis.incomeByCategory && Object.keys(analysis.incomeByCategory).length > 0 ? (
              <div className="category-list">
                {Object.entries(analysis.incomeByCategory)
                  .sort(([, a], [, b]) => b - a)
                  .map(([category, amount]) => (
                    <div key={category} className="category-item income">
                      <span className="category-name">{category}</span>
                      <span className="category-amount positive">+₹ {amount.toFixed(2)}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p style={{ color: '#999' }}>No income transactions found</p>
            )}
          </div>

          <div className="card">
            <h2>💸 Expense Categories</h2>
            {analysis.expensesByCategory && Object.keys(analysis.expensesByCategory).length > 0 ? (
              <div className="category-list">
                {Object.entries(analysis.expensesByCategory)
                  .sort(([, a], [, b]) => b.total - a.total)
                  .map(([category, data]) => (
                    <div key={category} className="category-item expense">
                      <div className="category-info">
                        <span className="category-name">{category}</span>
                        <span className="transaction-count">({data.count} transactions)</span>
                      </div>
                      <span className="category-amount negative">-₹ {data.total.toFixed(2)}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <p style={{ color: '#999' }}>No expense transactions found</p>
            )}
          </div>

          {statement && statement.transactions && statement.transactions.length > 0 && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: '0' }}>📝 All Transactions</h2>
                <button 
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    setSelectedTransactions(new Set());
                  }}
                  style={{
                    background: selectionMode ? '#667eea' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '14px'
                  }}
                >
                  {selectionMode ? '✓ Selection Mode ON' : '+ Enable Selection'}
                </button>
              </div>
              
              {selectionMode && (
                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedTransactions.size === statement.transactions.length && statement.transactions.length > 0}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '500' }}>
                      {selectedTransactions.size > 0 
                        ? `Selected ${selectedTransactions.size} of ${statement.transactions.length}` 
                        : `Select all ${statement.transactions.length} transactions`}
                    </span>
                  </label>
                </div>
              )}

              {selectionMode && selectedTransactions.size > 0 && getSelectionStats() && (
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '12px', 
                  borderRadius: '6px', 
                  marginBottom: '15px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px'
                }}>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: '500' }}>Total Debits</p>
                    <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                      -₹ {getSelectionStats().totalDebit.toFixed(2)}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
                      {getSelectionStats().debitCount} transactions
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: '500' }}>Total Credits</p>
                    <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                      +₹ {getSelectionStats().totalCredit.toFixed(2)}
                    </p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#999' }}>
                      {getSelectionStats().creditCount} transactions
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 4px 0', color: '#666', fontSize: '12px', fontWeight: '500' }}>Net Amount</p>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '18px', 
                      fontWeight: 'bold', 
                      color: getSelectionStats().total >= 0 ? '#28a745' : '#dc3545' 
                    }}>
                      {getSelectionStats().total >= 0 ? '+' : '-'}₹ {Math.abs(getSelectionStats().total).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              <div className="transactions-table">
                <div className="table-header">
                  {selectionMode && <div className="table-cell" style={{ flex: 0.5, textAlign: 'center' }}>✓</div>}
                  <div className="table-cell" style={{ flex: 2 }}>Date</div>
                  <div className="table-cell" style={{ flex: 3 }}>Description</div>
                  <div className="table-cell" style={{ flex: 2 }}>Category</div>
                  <div className="table-cell" style={{ flex: 2, textAlign: 'right' }}>Amount</div>
                  <div className="table-cell" style={{ flex: 1, textAlign: 'center' }}>Type</div>
                </div>
                {statement.transactions.map((txn, idx) => (
                  <div key={idx} className="table-row" style={{ 
                    background: selectionMode && selectedTransactions.has(idx) ? '#f0f7ff' : 'transparent'
                  }}>
                    {selectionMode && (
                      <div className="table-cell" style={{ flex: 0.5, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedTransactions.has(idx)}
                          onChange={() => handleTransactionSelect(idx)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    )}
                    <div className="table-cell" style={{ flex: 2 }}>{txn.date}</div>
                    <div className="table-cell" style={{ flex: 3, wordBreak: 'break-word' }}>{txn.description}</div>
                    <div className="table-cell" style={{ flex: 2 }}>{txn.category || 'Other'}</div>
                    <div className={`table-cell ${txn.type === 'credit' ? 'positive' : 'negative'}`} style={{ flex: 2, textAlign: 'right' }}>
                      {txn.type === 'credit' ? '+' : '-'}₹ {txn.amount.toFixed(2)}
                    </div>
                    <div className="table-cell" style={{ flex: 1, textAlign: 'center' }}>
                      <span className={`badge ${txn.type}`}>{txn.type}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StatementAnalyser;
